// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { ArrowUpRight12 } from '@mysten/icons';
import { type SuiValidatorSummary } from '@mysten/sui.js/client';
import { Heading, Text } from '@mysten/ui';

import { CopyToClipboard } from '~/ui/CopyToClipboard';
import { DescriptionList, DescriptionItem } from '~/ui/DescriptionList';
import { ImageIcon, isUsableImageSrc } from '~/ui/ImageIcon';
import { AddressLink, ObjectLink } from '~/ui/InternalLink';
import { tryFormatCpcAddress } from '~/utils/cpcAddress';
import {
	displayValidatorDescription,
	displayValidatorHeading,
} from '~/utils/displayValidator';

type ValidatorMetaProps = {
	validatorData: SuiValidatorSummary;
};

export function ValidatorMeta({ validatorData }: ValidatorMetaProps) {
	const validatorPublicKey = validatorData.protocolPubkeyBytes;
	const validatorName = displayValidatorHeading(validatorData.name, validatorData.description);
	const logo = validatorData.imageUrl;
	const description = displayValidatorDescription(validatorData.description);
	const displayAddress = tryFormatCpcAddress(validatorData.suiAddress);
	const displayPoolId = tryFormatCpcAddress(validatorData.stakingPoolId);
	const projectUrl = isUsableImageSrc(validatorData.projectUrl)
		? validatorData.projectUrl
		: '';

	return (
		<>
			<div className="flex basis-full gap-5 md:mr-7.5 md:basis-1/3">
				<ImageIcon src={logo} label={validatorName} fallback={validatorName} size="xl" />
				<div className="mt-1.5 flex flex-col">
					<Heading as="h1" variant="heading2/bold" color="dark">
						{validatorName}
					</Heading>
					{projectUrl && (
						<a
							href={projectUrl}
							target="_blank"
							rel="noreferrer noopener"
							className="mt-2.5 inline-flex items-center gap-1.5 text-body font-medium text-[#38BDF8] no-underline"
						>
							{projectUrl.replace(/\/$/, '')}
							<ArrowUpRight12 className="text-[#38BDF8]" />
						</a>
					)}
				</div>
			</div>
			<div className="min-w-0 basis-full break-words md:basis-2/3 bg-[#0F172A] px-4 py-2 rounded-xl hover:border border-[#38BDF8] transition-all duration-300 hover:shadow-[0_0_15px_rgba(56,189,248,0.5)]">
				<DescriptionList>
					<DescriptionItem title="Description" align="start">
						<Text variant="pBody/medium" color="dark">
							{description || '--'}
						</Text>
					</DescriptionItem>
					<DescriptionItem title="Location" align="start">
						<Text variant="pBody/medium" color="dark">
							--
						</Text>
					</DescriptionItem>
					<DescriptionItem title="Pool ID" align="start">
						<div className="flex items-start gap-1 break-all">
							<ObjectLink objectId={validatorData.stakingPoolId} noTruncate label={displayPoolId} />
							<CopyToClipboard size="md" color="steel" copyText={displayPoolId} />
						</div>
					</DescriptionItem>
					<DescriptionItem title="Address" align="start">
						<div className="flex items-start gap-1 text-[#38BDF8]">
							<AddressLink address={validatorData.suiAddress} noTruncate label={displayAddress} />
							<CopyToClipboard size="md" color="steel" copyText={displayAddress} />
						</div>
					</DescriptionItem>
					<DescriptionItem title="Public Key" align="start">
						<Text variant="pBody/medium" color="dark">
							{validatorPublicKey}
						</Text>
					</DescriptionItem>
				</DescriptionList>
			</div>
		</>
	);
}
